import { PkDivider } from '@uefadigital/panenka-react';
import useThrottledCallback from 'beautiful-react-hooks/useThrottledCallback';
import useWindowScroll from 'beautiful-react-hooks/useWindowScroll';
import clsx from 'clsx';
import { lazy, memo, Suspense, useEffect, useRef, useState } from 'react';

import { useFilters } from '../../hooks/filters';
import { useMatchesCalendarMode } from '../../hooks/mode';
import { useMatchesCalendarOptions } from '../../store';
import { MatchesCalendarMode } from '../../store/types';
import Header from '../Header';

import PromotionPanel from '@shared/PromotionPanel';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { competitionName } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const Bracket = lazy(() => import(/* webpackPrefetch: true */ '@shared/Bracket'));
const EuroBracket = lazy(() => import(/* webpackPrefetch: true */ '@shared/EuroBracket'));
const DatesList = lazy(() => import(/* webpackPrefetch: true */ '../DatesList'));
const MatchesList = lazy(() => import(/* webpackPrefetch: true */ '../MatchesList'));
const PhaseTabs = lazy(() => import(/* webpackPrefetch: true */ '../PhaseTabs'));
const CalendarTabs = lazy(() => import(/* webpackPrefetch: true */ '../CalendarTabs'));
const Footer = lazy(() => import(/* webpackPrefetch: true */ '../Footer'));

const Main = () => {
  const { options } = useMatchesCalendarOptions();
  const { mode } = useMatchesCalendarMode();
  const { filtersActive } = useFilters();
  const onWindowScroll = useWindowScroll();
  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  onWindowScroll(
    useThrottledCallback(
      () => {
        if (!options.stickyHeader || !headerRef.current) return;

        const sticky = headerRef.current.offsetTop > headerRef.current.getBoundingClientRect().top;
        setIsSticky(sticky);
      },
      [],
      100,
    ),
  );

  useEffect(() => {
    if (mode === MatchesCalendarMode.ROUNDS) {
      GoogleTagManagerHelper.addPageMeta({
        pageName: `/${competitionName}/matches/byround/`,
        contentSection3: 'round',
      });
    } else if (mode === MatchesCalendarMode.DATES) {
      GoogleTagManagerHelper.addPageMeta({ pageName: `/${competitionName}/matches/`, contentsection3: '' });
    }
  }, [mode]);

  const BracketComponent = options.useNewBracket ? Bracket : EuroBracket;

  const showHeader = !options.showAllMatches && mode !== MatchesCalendarMode.BRACKET;
  const showTabs = !filtersActive || mode === MatchesCalendarMode.DATES;
  const showFooter =
    !options.showAllMatches && ![MatchesCalendarMode.DATES, MatchesCalendarMode.BRACKET].includes(mode);

  return (
    <>
      {showHeader && (
        <div
          className={clsx(
            'pk-container',
            options.headerTheme === 'light' && 'pk-border-bottom',
            'pk-pb--m',
            styles.header,
            options.stickyHeader && styles.headerSticky,
            isSticky && styles.isSticky,
            options.cssClass,
          )}
          ref={headerRef}
          pk-theme={options.headerTheme}
        >
          <Header isSticky={isSticky} />
          {showTabs && (
            <>
              <PkDivider
                className={clsx(
                  'pk-col pk-col--span-12 pk-d-md--none',
                  styles.divider,
                  isSticky ? 'pk-my--0' : 'pk-my--xs',
                )}
              />
              <div className={clsx(styles.tabsContainer, 'pk-col pk-col--span-12')}>
                <Suspense>
                  <CalendarTabs />
                </Suspense>
              </div>
            </>
          )}
        </div>
      )}
      <div className='pk-container pk-bg--background pk-position--relative' pk-theme='light'>
        {options.usePhaseTabs && (
          <Suspense>
            <PhaseTabs />
          </Suspense>
        )}
        <Suspense fallback={<div className='skl-calendar__content' />}>
          {mode === MatchesCalendarMode.DATES && <DatesList />}
          {mode === MatchesCalendarMode.ROUNDS && <MatchesList />}
          {mode === MatchesCalendarMode.BRACKET && (
            <BracketComponent
              {...options}
              disclaimerTag={options.bracketDisclaimerTag}
              disclaimerTooltipTag={options.bracketDisclaimerTooltipTag}
              disclaimerUrl={options.bracketDisclaimerUrl}
            />
          )}
        </Suspense>
        {showFooter && (
          <Suspense>
            <Footer tabsRef={headerRef} />
          </Suspense>
        )}
      </div>
      {options.showSimulatorPromotion && (
        <div pk-theme='light'>
          <PromotionPanel />
        </div>
      )}
    </>
  );
};

export default memo(Main);
